import React, { ChangeEvent, CSSProperties, FocusEvent, MouseEvent, useState } from "react";
import { Link, Prompt } from "react-router-dom";
import { DateTime } from "luxon";
import ReactModal from "react-modal";

import withAuth from "../../withAuth";
import withTitle, { IViewTitleProps } from "../../withTitle";
import { V2 } from "../../../Api";
import { calculateAge, createDataUri, formatDateLocalNumeric, formatDateTimeLocalNumeric, formatName, formatTelephonePartial, getSetStateAsync } from "../../../Utilities";
import { documentSignatureKindToId, documentSignatureKindToText, documentSignatureTextToKind } from "../../../TextTables";
import { IAuthProps } from "../../AuthContext";
import { RouteComponentPropsHistory } from "../../../types";

import ConfirmationDialog from "../../ConfirmationDialog";
import DeletionConfirmationDialog from "../../DeletionConfirmationDialog";
import ViewToolbar from "../../ViewToolbar";
import ToolbarMenuItem from "../../ToolbarMenuItem";
import OverflowMenuItem from "../../OverflowMenuItem";
import InnerMenuBar from "../../InnerMenuBar";
import SignatureDialog, { ISignatureDialogReturnValues } from "./SignatureDialog";
import Checkbox from "../../Checkbox";
import TextTemplateDialog from "../../TextTemplateDialog";
import DocumentView from "../DocumentView";

import TagListInput2, { TagItemChange } from "../../TagListInput2";

import wcas_logo from "../../../images/wcas_logo_trim_small.png";

import "../../../form.css";
import "../../../dynamicform.css";

interface IPatientDocumentEditViewProps extends RouteComponentPropsHistory<any, any>, IAuthProps, IViewTitleProps {

}

type FormDataMap = { [id: string]: any };
type FormErrorMap = { [id: string]: boolean | string };
type DataListMap = { [id: string]: any[] };

type FunctionSpec = {
    name: string;
    parameters: Array<{
        type: "field";
        value: string;
    } | {
        type: "value";
        value: any;
    }>;
};

type DepenedentFieldMap = {
    [id: string]: Array<{
        field: string;
        function: FunctionSpec;
    }>;
};

interface IPatientDocumentEditViewState {
    isLoading: boolean | string;
    isSubmitting: boolean | string;
    hasChanged: boolean;
    isFinalizeDialogOpen: boolean;
    isDeleteDialogOpen: boolean;
    isSignDialogOpen: boolean;
    isAmending: boolean;
    isFromTemplate: boolean;
    isSaveTemplateDialogOpen: boolean;

    isTemplateDialogOpen: boolean;
    templateGroup: string | null;
    templateTargetFieldId: string | null;

    patientId: string;
    patient: any;
    allergies: any[];
    isReadOnly: boolean;
    isNewDocument: boolean;
    documentVersion: number;
    documentSignatures: any[];
    documentInfo: any | undefined;
    formSchema: any;
    formData: FormDataMap;
    formErrors: FormErrorMap;
    linkedFieldsHaveValue: { [id: string]: boolean };
    fieldSignatures: { [id: string]: any };
    linkedSources: { [name: string]: any };
    externalFileId: string | null;
    dataLists: DataListMap;
    dependentFields: DepenedentFieldMap;

    signatureMode: "document" | "field";
    inlineSignatureCallback: ((data: ISignatureDialogReturnValues) => void) | null;
    signatureDialogOptions: any[];
}

type ChangeHandlerEvent =
    ChangeEvent<HTMLInputElement> |
    ChangeEvent<HTMLTextAreaElement> |
    ChangeEvent<HTMLSelectElement>;

type FocusHandlerEvent =
    FocusEvent<HTMLInputElement> |
    FocusEvent<HTMLTextAreaElement> |
    FocusEvent<HTMLSelectElement>;

type FormDataContext = {
    dataSource: FormDataMap;
};

type DynamicFormContext = {
    headingLevel: number;
    nestPath: string[];
    repeaters: any[];
    contextStack: FormDataContext[];
    columnAlignments: string[] | null;
    columnBorders: string[] | null;
}


class PatientDocumentEditView extends React.Component<IPatientDocumentEditViewProps, IPatientDocumentEditViewState> {
    private setStateAsync = getSetStateAsync<IPatientDocumentEditViewState>();

    constructor(props: Readonly<IPatientDocumentEditViewProps>) {
        super(props);

        const state: IPatientDocumentEditViewState = {
            isLoading: true,
            isSubmitting: false,
            hasChanged: false,
            isFinalizeDialogOpen: false,
            isDeleteDialogOpen: false,
            isSignDialogOpen: false,
            isAmending: false,
            isFromTemplate: false,
            isSaveTemplateDialogOpen: false,

            isTemplateDialogOpen: false,
            templateGroup: null,
            templateTargetFieldId: null,

            patientId: props.match.params.patient_id || props.location.state?.patientId,
            patient: {},
            allergies: [],
            isReadOnly: !this.props.authContext.hasRight("document:edit"),
            isNewDocument: true,
            documentVersion: 0,
            documentInfo: undefined,
            documentSignatures: [],
            formSchema: {},
            formData: {},
            formErrors: {},
            linkedFieldsHaveValue: {},
            fieldSignatures: {},
            linkedSources: {},
            externalFileId: null,
            dataLists: {},
            dependentFields: {},

            signatureMode: "document",
            inlineSignatureCallback: null,
            signatureDialogOptions: [],
        };

        this.state = state;

        this.formInputChanged = this.formInputChanged.bind(this);
        this.formInputChangedNumber = this.formInputChangedNumber.bind(this);
        this.formInputChangedTelephone = this.formInputChangedTelephone.bind(this);
        this.formInputChangedYesNo = this.formInputChangedYesNo.bind(this);
        this.formInputChangedExclusiveCheckbox = this.formInputChangedExclusiveCheckbox.bind(this);
        this.formListInputChanged = this.formListInputChanged.bind(this);
        this.formInputBlur = this.formInputBlur.bind(this);
        this.goBack = this.goBack.bind(this);
        this.handleSaveHold = this.handleSaveHold.bind(this);
        this.handleSaveFinalize = this.handleSaveFinalize.bind(this);
        this.handleSaveContinue = this.handleSaveContinue.bind(this);
        this.toggleFinalizePrompt = this.toggleFinalizePrompt.bind(this);
        this.toggleDeletionDialog = this.toggleDeletionDialog.bind(this);
        this.beginFinalizeDocument = this.beginFinalizeDocument.bind(this);
        this.deleteDocument = this.deleteDocument.bind(this);
        this.toggleSignDialog = this.toggleSignDialog.bind(this);
        this.handleSignButton = this.handleSignButton.bind(this);
        this.openSignatureDialogInline = this.openSignatureDialogInline.bind(this);
        this.signDocumentField = this.signDocumentField.bind(this);
        this.amendDocument = this.amendDocument.bind(this);
        this.resolveLinkedData = this.resolveLinkedData.bind(this);
        this.closeTemplateDialog = this.closeTemplateDialog.bind(this);
        this.handleTemplateFinished = this.handleTemplateFinished.bind(this);
        this.lockDocument = this.lockDocument.bind(this);
        this.unlockDocument = this.unlockDocument.bind(this);
        this.handleSuggestionRequest = this.handleSuggestionRequest.bind(this);
        this.toggleSaveTemplateDialog = this.toggleSaveTemplateDialog.bind(this);
        this.saveAsTemplate = this.saveAsTemplate.bind(this);
    }

    public componentDidMount() {
        this.loadData();
    }

    public render() {
        if (this.state.isLoading === true) {
            return <div className="patient-page-body">
                <div className="spinner spinner-center lds-dual-ring-big" />
            </div>;
        }
        if (typeof this.state.isLoading === "string") {
            return <div className="patient-page-body">
                <div className="error-text">{this.state.isLoading}</div>
            </div>;
        }

        const toolbars = <>
            <DeletionConfirmationDialog isOpen={this.state.isDeleteDialogOpen} description={null} onRequestClose={this.toggleDeletionDialog} onConfirm={this.deleteDocument} onSuccess={this.goBack}>
                <p>Are you sure you want to delete this document?</p>
                <p>This action is permanent and <b>cannot be undone</b>.</p>
            </DeletionConfirmationDialog>
            {this.renderSignDialog()}
            {this.renderSaveTemplateDialog()}
            <ViewToolbar>
                <OverflowMenuItem>
                    <ToolbarMenuItem right="document:temp:new">
                        <button type="button" onClick={this.toggleSaveTemplateDialog}><i className="icon-file-text-o" /><span>Save as Template</span></button>
                    </ToolbarMenuItem>
                    <ToolbarMenuItem right="document:delete" condition={!this.state.isNewDocument}>
                        <button type="button" onClick={this.toggleDeletionDialog}><i className="icon-trash" /><span>Delete document</span></button>
                    </ToolbarMenuItem>
                </OverflowMenuItem>
            </ViewToolbar>
            <InnerMenuBar>
                <InnerMenuBar.Button icon="pencil" disabled={this.state.isNewDocument || !this.props.authContext.hasRight("document:sign") || !this.hasAvailableSignatures()} onClick={this.handleSignButton}>Sign</InnerMenuBar.Button>
                <InnerMenuBar.Button icon="print" disabled={true}>Print</InnerMenuBar.Button>
                {this.state.documentInfo?.lockedBy
                    ? <InnerMenuBar.Button icon="unlock" disabled={this.state.documentInfo?.lockedById !== this.props.authContext.user.employee.id && !this.props.authContext.hasRight("document:unlock")} onClick={this.unlockDocument}>Unlock</InnerMenuBar.Button>
                    : <InnerMenuBar.Button icon="lock" disabled={this.state.isFromTemplate || this.state.documentInfo?.isFinalized || this.state.documentInfo?.isSucceeded || !!this.state.externalFileId} onClick={this.lockDocument}>Lock</InnerMenuBar.Button>}
                <InnerMenuBar.Button icon="file-o" accentIcon="pencil" disabled={!this.state.documentInfo?.isFinalized || this.state.isAmending || this.state.documentInfo?.isSucceeded || !!this.state.externalFileId} onClick={this.amendDocument}>Amend</InnerMenuBar.Button>
                <InnerMenuBar.Button icon="save" disabled={this.state.documentInfo?.isFinalized || !!this.state.externalFileId || this.state.isSubmitting === true || !this.state.hasChanged} onClick={this.handleSaveContinue}>Save</InnerMenuBar.Button>
            </InnerMenuBar>
        </>;

        if (this.state.externalFileId) {
            // Embed file viewer
            return <>
                {toolbars}
                {this.renderExternalDocument()}
            </>;
        }

        const display = this.state.formSchema.display.edit;

        return (
            <div className="viewer-root">
                <Prompt when={this.state.hasChanged} message="Your changes will not be saved if you continue." />
                <ConfirmationDialog isOpen={this.state.isFinalizeDialogOpen} title="Save and finalize document?" description={null} onRequestClose={this.toggleFinalizePrompt} onConfirm={this.handleSaveFinalize} onSuccess={this.goBack}>
                    <div>Save and finalize this document?</div>
                    <br />
                    <div>This document will no longer be editable once it's finalized.</div>
                </ConfirmationDialog>
                <TextTemplateDialog isOpen={this.state.isTemplateDialogOpen} templateGroup={this.state.templateGroup || ""} onTemplateFinished={this.handleTemplateFinished} onRequestClose={this.closeTemplateDialog} onRequestResolveLink={this.resolveLinkedData} />
                {toolbars}
                {this.state.isAmending
                    ? <div className="status-box slim">You are amending this document. The original will remain, and a new copy with your changes will be saved.</div>
                    : this.state.documentInfo?.successionId
                        ? <div className="status-box slim">This document has been amended from its original form. <Link to={`${this.props.location.pathname}/versions`}>Other versions</Link></div>
                        : null}
                {this.state.documentInfo?.isSucceeded
                    ? <div className="warning-box slim">You are viewing a previous version of this document.</div>
                    : null}
                <form>
                    {this.state.documentInfo?.lockedBy
                        ? this.state.documentInfo?.lockedById === this.props.authContext.user.employee.id
                            ? <div className="warning-box"><i className="icon icon-lock" /> This document is currently locked by you. Others cannot edit this document until you unlock it.</div>
                            : <div className="warning-box"><i className="icon icon-lock" /> This document is currently locked by {formatName(this.state.documentInfo.lockedBy.name)}. You cannot edit this document until it's unlocked.</div>
                        : null}
                    {typeof this.state.isSubmitting === "string"
                        ? <div className="error-text">{this.state.isSubmitting}</div>
                        : null}
                    {this.renderForm(display)}
                    &nbsp;
                    {this.state.isSubmitting === true
                        ? <div className="spinner lds-dual-ring" />
                        : typeof this.state.isSubmitting === "string"
                            ? <div className="error-text">{this.state.isSubmitting}</div>
                            : null}
                    <div className="action-panel-dialog">
                        <span className="spring" />
                        {!this.state.isReadOnly
                            ? <>
                                <button type="button" className="button green" disabled={this.state.isSubmitting === true} onClick={this.beginFinalizeDocument}>Save &amp; Finalize</button>
                                <button type="button" className="button green" disabled={this.state.isSubmitting === true || (!this.state.hasChanged && !this.state.isNewDocument)} onClick={this.handleSaveHold}>Save &amp; Hold</button>
                            </>
                            : this.state.isFromTemplate
                                ? <>
                                    <button type="button" className="button green" disabled={this.state.isSubmitting === true} onClick={this.beginFinalizeDocument}>Save &amp; Finalize</button>
                                </>
                                : null}
                        <button type="button" className="button" onClick={this.goBack}>{this.state.hasChanged ? "Cancel" : "Go back"}</button>
                    </div>
                </form>
            </div>
        );
    }

    private renderForm(display: any) {
        const ctx: DynamicFormContext = {
            headingLevel: (display.headingLevel - 1) || 0,
            nestPath: [],
            repeaters: [],
            contextStack: [{
                dataSource: this.state.formData
            }],
            columnAlignments: null,
            columnBorders: null
        };

        return <div className="dynamicform">
            {this.renderDocumentHeader()}
            {this.renderFormContent(display.content, "form", ctx)}
            {this.renderSignatures()}

        </div>;
    }

    private renderFormContent(element: any, refId: string, ctx: DynamicFormContext): React.ReactNode {
        if (!element) {
            return null;
        }

        if (typeof element === "string") {
            // Inline text
            if (element.includes("\n")) {
                // Convert newlines to HTML breaks
                const stringParts = element.split("\n");
                const stringElements = [];
                for (let i = 0; i < stringParts.length; i++) {
                    if (i > 0) {
                        stringElements.push(<br key={`${refId}.${i}`} />);
                    }
                    stringElements.push(stringParts[i]);
                }

                return stringElements;
            }
            else {
                return element;
            }
        }

        const topContext = ctx.contextStack[ctx.contextStack.length - 1];

        //let dataSource = ctx.contextStack[0].dataSource;

        //if (!dataSource) {
        //    dataSource = this.state.formData;
        //}

        if (Array.isArray(element)) {
            return element.map((a: any, i: number) => this.renderFormContent(a, `${refId}.${i}`, ctx));
        }

        const classNames = [];

        if (element.line_height) {
            classNames.push(`line-height-${element.line_height}`);
        }

        if (element.width) {
            classNames.push(`width-${element.width < 1 ? element.width.toString().replace(".", "") : element.width}`);
        }

        if (element.indent) {
            classNames.push(`indent-${element.indent}`);
        }

        switch (element.type) {
            case "group":
                if (element.title) {
                    ctx.headingLevel++;
                }

                classNames.push("group");

                if (element.kind) {
                    classNames.push(`group-kind-${element.kind}`);
                }

                const groupContent = this.renderFormContent(element.content, refId, ctx);

                const groupElement = <div key={refId} className={classNames.join(" ")}>
                    {element.title ? this.headingLevel(element.level || ctx.headingLevel, element.title) : null}
                    {element.columns
                        ? <div className={`group-columns columns-${element.columns}`}>
                            {groupContent}
                        </div>
                        : groupContent}
                </div>;

                if (element.title) {
                    ctx.headingLevel--;
                }

                return groupElement;
            case "block":
                classNames.push("block");

                return <div key={refId} className={classNames.join(" ")}>{this.renderFormContent(element.content, refId, ctx)}</div>
            case "fieldgroup":
                classNames.push("field-group");
                if (element.fill) {
                    classNames.push(`fill`);
                }

                if (element.mixed_inline) {
                    classNames.push("mixed-inline");
                }

                const fieldElements = [
                    <div key={refId} className={classNames.join(" ")}>
                        {this.renderFormContent(element.content, refId, ctx)}
                    </div>];

                if (element.title) {
                    ctx.headingLevel++;
                    const title = this.headingLevel(element.level || ctx.headingLevel, element.title, `${refId}.title`);
                    fieldElements.unshift(title);
                    ctx.headingLevel--;
                }

                return fieldElements;
            case "field":
                classNames.push("input-group", `field-${this.state.formSchema.fields[element.id]?.type}`);

                if (element.print_order) {
                    classNames.push(`order-${element.print_order}`);
                }
                else {
                    switch (this.state.formSchema.fields[element.id]?.type) {
                        case "text-multiline":
                        case "yesno":
                        case "radio":
                            if (element.value === undefined) {
                                classNames.push("order-normal");
                            }
                            break;
                    }
                }

                if (element.long) {
                    classNames.push("input-long");
                }

                if (element.align) {
                    classNames.push(`text-align-${element.align}`);
                }

                return <span key={refId} className={classNames.join(" ")}>
                    {this.renderFormField(element, this.state.formSchema.fields[element.id], false, ctx)}
                </span>;
            case "inline-field":
                classNames.push("inline-field");

                if (element.align) {
                    classNames.push(`text-align-${element.align}`);
                }

                return <span key={refId} className={classNames.join(" ")}>
                    {this.renderFormField(element, this.state.formSchema.fields[element.id], true, ctx)}
                </span>;
            case "paragraph":
                return <p key={refId} className={classNames.join(" ")}>
                    {this.renderFormContent(element.content, refId, ctx)}
                </p>;
            case "bold":
                return <b key={refId}>{this.renderFormContent(element.content, refId, ctx)}</b>;
            case "italic":
                return <i key={refId}>{this.renderFormContent(element.content, refId, ctx)}</i>;
            case "underline":
                return <u key={refId}>{this.renderFormContent(element.content, refId, ctx)}</u>;
            case "list":
                classNames.push("form-list", `form-list-${element.list || "bullet"}`);
                const listContent = (element.content as any[]).map((a, i) => <li key={`${refId}.${i}`}>{this.renderFormContent(a, `${refId}.${i}`, ctx)}</li>);

                if (element.list === "number") {
                    return <ol key={refId} className={classNames.join(" ")} start={element.list_start}>{listContent}</ol>;
                }

                return <ul key={refId} className={classNames.join(" ")}>{listContent}</ul>;
            case "repeat":
                ctx.nestPath.push(element.id);
                ctx.repeaters.push(element);

                // `dataArray` is an array for repeat elements
                const dataArray = topContext.dataSource[element.id] as any[] || [];
                // const errorMap = topContext.errorSource[element.id]

                const repeatElements = dataArray.map((a, i) => {
                    ctx.nestPath.push(i.toString());

                    ctx.contextStack.push({
                        dataSource: a
                    });

                    const renderedContent = this.renderFormContent(element.content, `${refId}.${i}`, ctx);
                    ctx.nestPath.pop();
                    ctx.contextStack.pop();

                    return renderedContent;
                });

                ctx.repeaters.pop();
                ctx.nestPath.pop();
                return repeatElements;
            case "table":
                classNames.push("form-table", "nohover");

                if (element.striped) {
                    switch (element.striped) {
                        case "even": classNames.push("striped"); break;
                        case "odd": classNames.push("striped-odd"); break;
                    }
                }
                if (element.border === "none") {
                    classNames.push("no-border");
                }

                ctx.columnAlignments = element.columns.map((a: any) => a.align);
                ctx.columnBorders = element.columns.map((a: any) => a.border);

                const tableHeadElements = element.columns.map((a: any, i: number) => <th key={`${refId}.th.${i}`} className={`width-${a.width < 1 ? a.width.toString().replace(".", "") : a.width}${/*a.align ? ` col-align-${a.align}` : ""*/""}`}>{a.content}</th>);
                const tableBodyElements = this.renderFormContent(element.body, `${refId}.head`, ctx);

                ctx.columnAlignments = null;
                ctx.columnBorders = null;

                return <table key={refId} className={classNames.join(" ")}>
                    <thead>
                        <tr>{tableHeadElements}</tr>
                    </thead>
                    <tbody>
                        {tableBodyElements}
                    </tbody>
                </table>;
            case "table-row":
                const alignments = ctx.columnAlignments || [];
                const borders = ctx.columnBorders || [];
                const rowspans = element.rowspan;
                const colspans = element.colspan;

                const tableRowElements = (element.content as any[]).map((a, i) => a.type === "table-cell"
                    ? this.renderFormContent(a, `${refId}.${i}`, ctx)
                    : <td key={`${refId}.${i}`} className={(alignments[i] ? `col-align-${alignments[i]}` : "") + (borders[i] ? ` border-${borders[i]}` : "")} rowSpan={rowspans?.[i]} colSpan={colspans?.[i]}>{this.renderFormContent(a, `${refId}.${i}`, ctx)}</td>
                );

                const border = element.border;
                if (border) {
                    if (typeof border === "string") {
                        classNames.push(`border-${border}`);
                    }
                    else {
                        for (const item of (border as string[])) {
                            classNames.push(`border-${item}`);

                        }
                    }
                }

                return <tr key={refId} className={classNames.join(" ")}>
                    {tableRowElements}
                </tr>;
            case "table-cell":
                if (element.border) {
                    if (typeof element.border === "string") {
                        classNames.push(`border-${element.border}`);
                    }
                    else {
                        for (const item of (element.border as string[])) {
                            classNames.push(`border-${item}`);

                        }
                    }
                }
                if (element.align) {
                    classNames.push(`col-align-${element.align}`);
                }

                return <td key={refId} className={classNames.join(" ")} colSpan={element.colspan} rowSpan={element.rowspan}>{this.renderFormContent(element.content, `${refId}.td`, ctx)}</td>;
            case "spacer":
                classNames.push("spacer");

                if (element.width) {
                    classNames.push(`width-${element.width < 1 ? element.width.toString().replace(".", "") : element.width}`);
                }

                return <span key={refId} className={classNames.join(" ")} />;
            default:
                return <span key={refId}>Unknown element {JSON.stringify(element)}</span>;
        }
    }

    private renderFormField(element: any, field: any, isInline: boolean, ctx: DynamicFormContext) {
        if (!field) {
            return <div className="field-missing">
                Field <code>{element.id}</code> is missing from data model!
            </div>;
        }

        let title = element.title || field.title;
        if (title === undefined) {
            title = `TITLE NOT DEFINED (${element.id})`;
        }

        const topContext = ctx.contextStack[ctx.contextStack.length - 1];

        let elementId = ctx.nestPath.length > 0
            ? `${ctx.nestPath.join("/")}/${element.id}`
            : element.id;

        let changeHandler = this.formInputChanged;
        let value = topContext.dataSource[element.id];
        if (value === undefined || value == null) {
            value = "";
        }
        let inputType = field.type;
        let inputOptions = field.values as any[] | undefined;
        let canUseDatalist = true;
        switch (inputType) {
            case "numeric":
                inputType = "tel";
                break;
            case "telephone":
                inputType = "tel";
                value = formatTelephonePartial(value);
                changeHandler = this.formInputChangedTelephone;
                break;
            case "signature":
                value = this.state.fieldSignatures[elementId];
                break;
            case "number":
                changeHandler = this.formInputChangedNumber;
                break;
            case "yesno":
                changeHandler = this.formInputChangedYesNo;
                if (!inputOptions) {
                    inputOptions = [
                        { title: "Yes", value: "yes", dataValue: true },
                        { title: "No", value: "no", dataValue: false }
                    ];
                }
                inputType = "radio";
                break;
            case "radio":
                changeHandler = this.formInputChangedExclusiveCheckbox;
                break;
            case "inline-list":
            case "stacked-list":
                if (!value) {
                    value = [];
                }
                canUseDatalist = false;
                break;
        }

        const style: CSSProperties = {};

        if (field.format?.transform) {
            style.textTransform = field.format?.transform
        }

        // TODO: Handle element-level computed default values
        // let defaultValue = this.computeDefaultValue(element.default || field.default);

        if (ctx.repeaters.length > 0) {
            // We're inside a repeater
            const repeater = ctx.repeaters[ctx.repeaters.length - 1];

            const repeaterArray = ctx.nestPath
                .slice(0, -1)
                .reduce((a, v) => a[v!], this.state.formData) as any[];
            const ourIndex = ctx.nestPath[ctx.nestPath.length - 1];
            const isLastItem = +ourIndex === repeaterArray.length - 1;

            if (isLastItem) {
                const originalChangeHandler = changeHandler;
                changeHandler = (e) => {
                    if (!repeater.max || repeaterArray.length < repeater.max) {
                        // Create a new array row
                        repeaterArray.push({});

                        this.setState({
                            formData: this.state.formData
                        });
                    }

                    originalChangeHandler(e);
                };
            }
        }

        const error = this.state.formErrors[elementId];
        let className = error ? "input-error" : undefined;

        let isReadOnly = element.readonly || field.readonly || this.state.isReadOnly || (field.editable_if_linked === false && this.state.linkedFieldsHaveValue[element.id]);

        let input: React.ReactNode;
        switch (inputType) {
            case "multiple-options":
                input = <select id={elementId} name={elementId} value={value} className={className} style={style} onChange={changeHandler} onBlur={this.formInputBlur} disabled={isReadOnly}>
                    <option value=""></option>
                    {field.values.map((a: any) => <option key={`${field.id}.${a.value}`} value={a.value}>{a.title}</option>)}
                </select>;
                break;
            case "inline-list":
                input = <TagListInput2
                    className={className} style={style} disabled={isReadOnly}
                    name={elementId} items={value}
                    addOnBlur={true} mode="inline"
                    onItemsChange={this.formListInputChanged}
                    onRequestSuggestions={text => this.handleSuggestionRequest(field, text)} />
                break;
            case "stacked-list":
                input = <TagListInput2
                    className={className} style={style} disabled={isReadOnly}
                    name={elementId} items={value}
                    addOnBlur={true} mode="stacked"
                    onItemsChange={this.formListInputChanged}
                    onRequestSuggestions={text => this.handleSuggestionRequest(field, text)} />
                break;
            case "signature":
                input = <button type="button" id={elementId} name={elementId} className={`input-signature ${className || ""}`} disabled={isReadOnly || this.state.isNewDocument} onClick={this.openSignatureDialogInline}>
                    {value
                        ? this.renderFormSignature(value)
                        : !isReadOnly
                            ? !this.state.isNewDocument
                                ? <span className="print-hide"><i className="icon-pencil" /> Click to sign...</span>
                                : <span className="print-hide"><i><small>Save document before signing</small></i></span>
                            : null}
                </button>;
                break;
            /*case "yesno":
                input = <span className="input-radio">
                    <label><input type="radio" value="yes" name={elementId} checked={value === true} className={className} style={style} onChange={changeHandler} disabled={isReadOnly} />Yes</label>
                    <label><input type="radio" value="no" name={elementId} checked={value === false} className={className} style={style} onChange={changeHandler} disabled={isReadOnly} />No</label>
                </span>;
                break;*/
            case "radio":
                if ('value' in element) {
                    // Single value of a radio group
                    const opt = inputOptions!.find(a => a.value === element.value);
                    if (opt) {
                        element.show_title = false;
                        input = <Checkbox key={`${elementId}-${opt.value}`} fancy={true} fancyStyle={element.kind || field.kind} type="checkbox" value={opt.value} name={elementId} id={`${elementId}-${opt.value}`} checked={value === ("dataValue" in opt ? opt.dataValue : opt.value)} className={className} style={style} onChange={changeHandler} disabled={isReadOnly}>{opt.title}</Checkbox>;
                    }
                    else {
                        input = <span className="field-missing">Field '{element.id}' value '{element.value}' could not be found</span>;
                    }
                }
                else {
                    input = <span className={element.orientation === "vertical" ? "input-radio radio-vertical" : "input-radio"}>
                        {/*inputOptions.map((opt: any) => <label key={opt.value}><input type="radio" value={opt.value} name={elementId} checked={value === ("dataValue" in opt ? opt.dataValue : opt.value)} className={className} style={style} onChange={changeHandler} disabled={isReadOnly} />{opt.title}</label>)*/}
                        {inputOptions!.map((opt: any) => <Checkbox key={`${elementId}-${opt.value}`} fancy={true} fancyStyle={element.kind || field.kind} type="checkbox" value={opt.value} name={elementId} id={`${elementId}-${opt.value}`} checked={value === ("dataValue" in opt ? opt.dataValue : opt.value)} className={className} style={style} onChange={changeHandler} disabled={isReadOnly}>{(element.show_title === false) ? "\u200B" : opt.title}</Checkbox>)}
                    </span>;
                }
                break;
            case "text-multiline":
                if (element.lines) {
                    style.height = `${20 * element.lines}px`;
                }

                input = isReadOnly
                    ? <div className="text-finalized">{value}</div>
                    : <>
                        <textarea id={elementId} name={elementId} value={value} className={className} style={style} onChange={changeHandler} onBlur={this.formInputBlur} maxLength={field.validation?.max_length} minLength={field.validation?.min_length} disabled={isReadOnly} />
                        {field.template_group
                            ? <button type="button" className="button text-with-icon" onClick={() => this.openTemplateDialog(field.id, field.template_group)}><i className="icon-plus" /><span>Add templated text</span></button>
                            : null}
                    </>;
                break;
            case "checkbox":
                isInline = true;
                input = <Checkbox fancy={true} fancySize={"normal"} id={elementId} name={elementId} checked={value} className={className} style={style} onChange={changeHandler} onBlur={this.formInputBlur} disabled={isReadOnly} autoComplete="off">{(element.show_title === false) ? "\u200B" : (title || "\u00A0")}</Checkbox>;
                break;
            default:
                input = <input id={elementId} name={elementId} value={value} className={className} type={inputType} style={style} onChange={changeHandler} onBlur={this.formInputBlur} maxLength={field.validation?.max_length} minLength={field.validation?.min_length} disabled={isReadOnly} autoComplete="off" list={field.suggestions ? `${elementId}_list` : undefined} />;
                break;
        }

        if (canUseDatalist) {
            if (typeof field.suggestions === "string") {
                input = <>
                    {input}
                    <datalist id={`${elementId}_list`}>
                        {this.state.dataLists[field.suggestions]?.map((a: any) => <option key={`${field.id}.${a.value}`}>{a.value}</option>)}
                    </datalist>
                </>;
            }
            else if (Array.isArray(field.suggestions)) {
                input = <>
                    {input}
                    <datalist id={`${elementId}_list`}>
                        {field.suggestions.map((a: any) => <option key={`${field.id}.${a}`}>{a}</option>)}
                    </datalist>
                </>;
            }
        }

        if (isInline) {
            return input;
        }
        else {
            return <>
                {element.show_title !== false ? <label htmlFor={elementId}>{title}</label> : null}
                {input}
                {error ? <span className="input-error-message">Invalid entry</span> : null}
            </>;
        }
    }

    private headingLevel(level: number, content: any, refId?: string) {
        switch (level) {
            case 1: return <h1 key={refId}>{content}</h1>;
            case 2: return <h2 key={refId}>{content}</h2>;
            case 3: return <h3 key={refId}>{content}</h3>;
            case 4: return <h4 key={refId}>{content}</h4>;
            case 5: return <h5 key={refId}>{content}</h5>;
            case 6: return <h6 key={refId}>{content}</h6>;
            default: return <h3 key={refId}>{content}</h3>;
        }
    }

    private renderSignatures(newWay = true) {
        if (newWay) {
            if (this.state.documentSignatures.length === 0) {
                return null;
            }

            return <div className="dynamicform-signatures">
                {this.state.documentSignatures.map(a => <div key={a.fieldId}>
                    {a.timestamp
                        ? a.type === 0
                            ? <em>{`${a.title}: Electronically signed by ${formatName(a.employee.name)} on ${formatDateTimeLocalNumeric(a.timestamp)}`}</em>
                            : <b>IMAGE SIGNATURES NOT YET DISPLAYABLE</b>
                        : a.isRequired
                            ? <b>Not signed by {a.title}</b>
                            : `Not signed by ${a.title}`}
                </div>)}
            </div>;
        }
        else {
            if (!this.state.documentInfo) {
                return null;
            }

            const signatures = this.state.documentInfo?.signatures as any[] || [];
            if (signatures.length === 0) {
                return null;
            }

            return <div className="dynamicform-signatures">
                {signatures.filter(a => !a.fieldId).map(a => <div key={a.id}>
                    Signed as {documentSignatureKindToText(a.kind)} by <b>{formatName(a.employee.name)}</b> on {formatDateTimeLocalNumeric(a.timestamp)}
                </div>)}
            </div>;
        }
    }

    private renderSignDialog() {
        return <SignatureDialog
            isOpen={this.state.isSignDialogOpen} onRequestClose={this.toggleSignDialog}
            allowDocumentSign={this.state.isReadOnly}
            mode={"field"} availableSignatures={this.state.signatureDialogOptions}
        />
    }

    private renderFormSignature(signature: any) {
        switch (signature.type) {
            case 0://"electronic":
                return <>Signed by {formatName(signature.employee.name)}</>;
            case 1://"image":
                // TODO: Handle vector signature differently?
                if (!signature.data) {
                    return <><span className="spinner lds-dual-ring-small" /></>
                }

                return <><img src={createDataUri(signature.mime, signature.data)} alt="Signature" /></>;
            default:
                return null;
        }
    }

    private renderDocumentHeader() {
        const addr = this.state.patient.address;

        return <header className="document-header">
            <div className="document-logo">
                <img src={wcas_logo} alt="" />
            </div>
            <div className="spring" />
            <div className="document-header-right">
                <span className="document-meta">
                    {formatName(this.state.patient.name)} | {formatDateLocalNumeric(this.state.patient.birthdate)} | {this.state.patient.chartNumber}<br />

                    {addr
                        ? addr.line2
                            ? `${addr.line1} ${addr.line2}, ${addr.city}, ${addr.state} ${addr.zip}`
                            : `${addr.line1}, ${addr.city}, ${addr.state} ${addr.zip}`
                        : "No address on file"}
                    <div>
                        <b>Allergies: </b>
                        {this.state.allergies.length > 0
                            ? this.state.allergies.map(a => a.value).reduce((prev, cur) => `${prev}, ${cur}`)
                            : "None listed"
                        }
                    </div>
                </span>
                <span className="document-title">{this.state.formSchema.meta.title}</span>
            </div>
        </header>;
    }

    private renderExternalDocument() {
        return <DocumentView fileId={this.state.externalFileId}>
            {this.renderSignatures()}
        </DocumentView>;
    }

    private formInputChangedYesNo(e: ChangeHandlerEvent) {
        this.formInputChangedExclusiveCheckbox(e, value => value === "yes" ? true : value === "no" ? false : undefined);
    }

    private formInputChangedNumber(e: ChangeHandlerEvent) {
        this.formInputChanged(e, value => value === "" ? null : +value);
    }

    private formInputChangedTelephone(e: ChangeHandlerEvent) {
        this.formInputChanged(e, value => value?.replace(/[^0-9]/g, ""));
    }

    private formInputChangedExclusiveCheckbox(e: ChangeHandlerEvent, converter?: (value: any) => any) {
        const name = e.currentTarget.name;
        const value = e.currentTarget.value;
        const checked = (e.currentTarget.type === "checkbox") ? (e.currentTarget as HTMLInputElement).checked : e.currentTarget.value;

        this.formInputSetValue(name, checked ? value : undefined, converter);
    }

    private formInputChanged(e: ChangeHandlerEvent, converter?: (value: any) => any) {
        let name = e.currentTarget.name;
        const value = (e.currentTarget.type === "checkbox") ? (e.currentTarget as HTMLInputElement).checked : e.currentTarget.value;

        this.formInputSetValue(name, value, converter);
    }

    private formListInputChanged(change: TagItemChange, name?: string) {
        if (!name) {
            console.error("Name was not set for list input");
            return;
        }

        // Apply changes to a copy of the item list
        const [data, property] = this.lookupFormDataPath(this.state.formData, name);
        let currentItems = data[property] as any[] || [];
        if (change.action === "add") {
            (currentItems = currentItems.slice(0)).push(change.value);
        }
        else if (change.action === "remove") {
            currentItems = currentItems.filter((_, index) => index !== change.index);
        }

        this.formInputSetValue(name, currentItems);
    }

    private formInputSetValue(name: string, value: any, converter?: (value: any) => any) {
        const { formData, formErrors, dependentFields } = this.state;

        if (converter) {
            value = converter(value);
        }

        if (name.includes("/")) {
            // Nested value
            const [data, property] = this.lookupFormDataPath(formData, name);

            if (value === undefined) {
                delete data[property];
            }
            else {
                data[property] = value;
            }
        }
        else {
            // Direct value
            if (value === undefined) {
                delete formData[name];
            }
            else {
                formData[name] = value;
            }
        }

        if (formErrors[name]) {
            delete formErrors[name];
        }

        // Re-compute dynamic fields that depend on this field
        if (dependentFields[name]) {
            for (const func of dependentFields[name]) {
                formData[func.field] = this.computeFunctionValue(func.function);
            }
        }

        this.setState({
            hasChanged: true,
            formData
        });
    }

    private lookupFormDataPath(data: any, path: string) {
        const pathParts = path.split("/");
        for (const pathPart of pathParts.slice(0, -1)) {
            if (!data) {
                break;
            }

            data = data[pathPart];
        }

        return [data, pathParts[pathParts.length - 1]];
    }

    private formInputBlur(e: FocusHandlerEvent, errorSource?: FormErrorMap) {
        let name = e.currentTarget.name;

        const error = this.validateField(name, false);

        if (typeof error === "boolean") {
            const formErrors = this.state.formErrors;

            if (error) {
                formErrors[name] = error;
            }
            else {
                delete formErrors[name];
            }

            this.setState({
                formErrors
            });
        }
    }

    private handleSuggestionRequest(field: any, text: string) {
        let suggestionList: any[];
        if (typeof field.suggestions === "string") {
            suggestionList = this.state.dataLists[field.suggestions];
        }
        else if (Array.isArray(field.suggestions)) {
            suggestionList = field.suggestions.map((a: string) => ({ value: a }));
        }
        else {
            return [];
        }

        if (!text) {
            return suggestionList;
        }

        text = text.toLowerCase();
        return suggestionList
            .filter(a => a.value.toLowerCase().includes(text) || (a.alternative && a.alternative.toLowerCase().includes(text)))
            .map(a => a.value);
    }

    private validateField(name: string, final: boolean) {
        let fieldName = name;
        let dataSource = this.state.formData;
        let isNested = fieldName.includes("/");
        if (isNested) {
            const lastSlash = name.lastIndexOf("/");
            fieldName = fieldName.substring(lastSlash + 1);
        }

        if (this.state.formSchema.fields[fieldName]?.validation) {
            let result = false;

            if (isNested) {
                // Drill down to get the nearest data source for validation
                dataSource = name.split("/").slice(0, -1).reduce((a, v) => a[v], dataSource);
            }
            const value = dataSource[fieldName] || "";
            const validation = this.state.formSchema.fields[fieldName].validation;

            if ((final || value) && validation.pattern) {
                // Regex pattern match
                result = !value.match(validation.pattern);
            }
            if (!result && (final || value) && validation.min_length > 0) {
                result = value.length < validation.min_length;
            }
            if (!result && value && validation.max_length > 0) {
                result = value.length > validation.max_length;
            }

            return result;
        }

        return undefined;
    }

    private validateForm(final = true) {
        const formErrors: { [name: string]: string | boolean } = {};
        for (const field of Object.keys(this.state.formSchema.fields)) {
            if (this.state.formSchema.fields[field].parent) {
                // Member of a repeater group
                const parent = this.state.formSchema.fields[field].parent;
                const rows = this.state.formData[parent];
                for (let i = 0; i < rows.length; i++) {
                    const rowName = `${parent}/${i}/${field}`;
                    const rowError = this.validateField(rowName, final);
                    if (rowError === undefined) {
                        // No validation settings
                        break;
                    }

                    if (rowError) {
                        formErrors[rowName] = rowError;
                    }
                }

                continue;
            }

            const error = this.validateField(field, final);
            if (error === undefined) {
                continue; // No validation settings
            }

            if (error) {
                formErrors[field] = error;
            }
        }

        this.setState({
            formErrors
        });

        return Object.keys(formErrors).length === 0;
    }

    private loadPatientId() {
        return new Promise<void>((resolve, reject) => {
            if (this.props.match.params.patient_id) {
                this.setState({
                    patientId: this.props.match.params.patient_id
                }, () => resolve());
                return;
            }

            if (this.props.match.params.surgery_id) {
                // Look up patient ID from surgery
                return V2.get<any>(`surgery/${this.props.match.params.surgery_id}/patient`)
                    .then((info) => {
                        this.setState({
                            patientId: info.patientId
                        }, () => resolve());
                    });
            }
        });
    }

    private async loadData() {
        try {
            this.setState({
                isLoading: true
            });

            await this.loadPatientId();

            let doc: any = undefined;

            if (this.props.match.params.documentId) {
                // Loading an existing document
                doc = await V2.get<any>(`document/${this.props.match.params.documentId}`);
                if (doc.fileId) {
                    await this.loadExternalDocument(doc);
                }
                else {
                    await this.loadDynamicDocument(doc);
                }
            }
            else {
                // New document
                await this.loadDynamicDocument(undefined);
            }
        }
        catch (e) {
            this.setState({
                isLoading: e.message || "Could not load data"
            });
        }
    }

    private async loadDynamicDocument(doc: any) {
        const [patient, allergiesResult] = await Promise.all([
            V2.get<any>(`patients/${this.state.patientId}`),
            V2.get<any>(`patients/${this.state.patientId}/allergies`)
        ]);

        let documentTypeId;

        let loadedData = {};
        if (doc) {
            // Loading an existing document
            documentTypeId = doc.documentTypeId;

            loadedData = JSON.parse(doc.data);
            delete doc.data;
        }
        else if (this.props.match.params.documentType) {
            // Creating a new document
            documentTypeId = this.props.match.params.documentType;
        }

        // TODO: Support loading a specific document version, likely through props or route state
        const documentType = await V2.get<any>(`document/types/${documentTypeId}` + (doc?.documentVersion ? `?version=${doc.documentVersion}` : ""));
        const schema = JSON.parse(documentType.schema);
        let shouldBeReadonly = false;
        let isFromTemplate = false;

        if (this.props.location.state?.templateId) {
            // Load document template
            const template = await V2.get<any>(`document/types/${documentTypeId}/templates/${this.props.location.state?.templateId}`);

            loadedData = JSON.parse(template.data);
            shouldBeReadonly = template.isFinalized;
            isFromTemplate = true;
        }

        if (doc?.signatures) {
            // Combine known signature slots with existing signatures on the document
            for (const signature of documentType.signatures) {
                const foundSignature = doc.signatures.find((a: any) => a.fieldId === signature.fieldId);
                if (foundSignature) {
                    const { kind, fieldId, ...signatureParts } = foundSignature;
                    Object.assign(signature, signatureParts);
                }
            }

            documentType.signatures.sort((a: any, b: any) => !a.timestamp
                ? 1
                : !b.timestamp
                    ? -1
                    : (a.timestamp || "").localeCompare(b.timestamp || ""));
        }

        const linkedSources = await this.loadLinkedSources(schema);
        const [data, linkedFieldsHaveValue] = this.createDefaultFormData(schema, linkedSources);

        Object.assign(data, loadedData);

        // Load field signatures into a map for lookup later
        const fieldSignatures: { [id: string]: any } = {};
        if (doc?.signatures) {
            for (const signature of doc.signatures) {
                if (signature.fieldId) {
                    fieldSignatures[signature.fieldId] = signature;
                }
            }
        }

        // Check for linked data lists and functions
        const listUseMap: { [id: string]: boolean } = {};
        const funcMap: DepenedentFieldMap = {};
        for (const fieldId of Object.keys(schema.fields)) {
            const field = schema.fields[fieldId];
            if (typeof field.suggestions === "string") {
                listUseMap[field.suggestions] = true;
            }

            if (field.function) {
                for (const param of field.function.parameters) {
                    if (param.type === "field") {
                        if (!funcMap[param.value]) {
                            funcMap[param.value] = [];
                        }

                        funcMap[param.value].push({
                            field: field.id,
                            function: field.function
                        });
                    }
                }
            }
        }

        const listMap: DataListMap = {};
        if (Object.keys(listUseMap).length > 0) {
            const listIds = await V2.get<any>(`list/resolve?locator=${Object.keys(listUseMap).join("&locator=")}`);
            for (const item of listIds.lists) {
                const list = await V2.get<any>(`list/type/${item.type}/${item.id}`);

                if (item.type !== "data-list") {
                    listMap[`${item.type}/${item.resourceLocator}`] = list.items;
                }
                else {
                    listMap[`${item.resourceLocator}`] = list.items;
                }
            }
        }

        this.setState({
            isLoading: false,
            isReadOnly: shouldBeReadonly || !this.props.authContext.hasRight("document:edit") || !!doc?.isFinalized || (doc?.lockedBy && doc?.lockedById !== this.props.authContext.user.employee.id),
            isNewDocument: !doc,
            isFromTemplate,
            patient,
            allergies: (allergiesResult.allergies as any[]).sort((a, b) => b.severity - a.severity),
            documentVersion: documentType.version,
            documentSignatures: documentType.signatures,
            documentInfo: doc,
            formSchema: schema,
            formData: data,
            linkedFieldsHaveValue,
            fieldSignatures,
            linkedSources,
            dataLists: listMap,
            dependentFields: funcMap
        }, () => {
            if (doc) {
                this.validateForm(false);
            }

            this.loadImageSignatures();

            this.props.viewTitleContext.changeTitle(`Edit Document \u2014 ${this.state.formSchema.meta.title}`);
        });
    }

    private async loadExternalDocument(doc: any) {
        const documentType = await V2.get<any>(`document/types/${doc.documentTypeId}` + (doc.documentVersion ? `?version=${doc.documentVersion}` : ""));

        if (doc?.signatures) {
            // Combine known signature slots with existing signatures on the document
            for (const signature of documentType.signatures) {
                const foundSignature = doc.signatures.find((a: any) => a.fieldId === signature.fieldId);
                if (foundSignature) {
                    const { kind, fieldId, ...signatureParts } = foundSignature;
                    Object.assign(signature, signatureParts);
                }
            }

            documentType.signatures.sort((a: any, b: any) => !a.timestamp
                ? 1
                : !b.timestamp
                    ? -1
                    : (a.timestamp || "").localeCompare(b.timestamp || ""));
        }

        this.setState({
            isLoading: false,
            documentInfo: doc,
            isReadOnly: true,
            isNewDocument: false,
            externalFileId: doc.fileId,
            documentSignatures: documentType.signatures
        });
    }

    private async loadLinkedSources(schema: any) {
        if (!schema.fields) {
            return {};
        }

        const sources: { [name: string]: any } = {};

        // Figure out which linked data sources need to be loaded
        for (const fieldId of Object.keys(schema.fields)) {
            const field = schema.fields[fieldId];
            if (field.link) {
                const linkInfo = this.parseDataLink(field.link);

                sources[linkInfo.source] = true;
            }
        }

        for (const source of Object.keys(sources)) {
            sources[source] = await this.loadSource(source);
        }

        return sources;
    }

    private async loadSource(source: string) {
        let data;
        switch (source) {
            case "patient":
                data = await V2.get<any>(`patients/${this.state.patientId}?include=allergies`);

                // Generate virtual properties
                data.age = calculateAge(data.birthdate);
                data.allergies.list = data.allergies.length > 0
                    ? data.allergies
                        .map((a: any) => a.value)
                        .reduce((prev: string, current: string) => `${prev}, ${current}`)
                    : "";

                const addr = data.address;
                if (addr) {
                    data.address.full = addr.line2
                        ? `${addr.line1} ${addr.line2}, ${addr.city}, ${addr.state} ${addr.zip}`
                        : `${addr.line1}, ${addr.city}, ${addr.state} ${addr.zip}`;
                }
                break;
            case "surgery":
                if (!this.props.match.params.surgery_id) {
                    // Not linked to a surgery
                    break;
                }

                data = await V2.get<any>(`surgery/${this.props.match.params.surgery_id}`);
                data.date = data.date?.substring(0, 10);

                break;
            case "user":
                const employee = this.props.authContext.user.employee as any;
                data = {
                    name: employee.name
                };
                break;
        }

        return data;
    }

    private async resolveLinkedData(link: string) {
        const { source, path } = this.parseDataLink(link);
        const { linkedSources } = this.state;

        if (!linkedSources[source]) {
            // Load source
            linkedSources[source] = await this.loadSource(source);

            await this.setStateAsync({
                linkedSources
            });
        }

        // We have the data source; find the property
        const pathParts = path.slice(1).split("/");
        let value = linkedSources[source];
        for (const pathPart of pathParts) {
            if (!value) {
                break;
            }

            value = value[pathPart];
        }

        return value;
    }

    private async loadImageSignatures() {
        const fieldSignatures = this.state.fieldSignatures;
        for (const key of Object.keys(fieldSignatures)) {
            const fieldSig = fieldSignatures[key];
            if (fieldSig.type !== 1 || fieldSig.data) {
                continue; // Doesn't need loading
            }

            try {
                const signature = await V2.get<any>(`document/${this.props.match.params.documentId}/signature/${fieldSig.id}`);
                fieldSignatures[key] = signature;
            }
            catch (e) {
                // Couldn't load signature, skip for now
            }
        }

        this.setState({
            fieldSignatures
        });
    }

    private createDefaultFormData(schema: any, linkedSources: { [name: string]: any }) {
        if (!schema.fields) {
            return [];
        }


        const dataModel: { [id: string]: any } = {};
        const linkedHaveValues: { [id: string]: boolean } = {};

        for (const fieldName of Object.keys(schema.fields)) {
            const field = schema.fields[fieldName];
            let defaultValue: any;
            if (field.type === "repeat") {
                // Repeaters default to an array containing a single object
                defaultValue = [{}];
            }

            if (field.link) {
                const linkInfo = this.parseDataLink(field.link);

                if (linkedSources[linkInfo.source]) {
                    const pathParts = linkInfo.path.slice(1).split("/");
                    let value = linkedSources[linkInfo.source];
                    for (const pathPart of pathParts) {
                        if (!value) {
                            break;
                        }

                        value = value[pathPart];
                    }

                    if (field.link_map) {
                        // Convert value according to map
                        value = field.link_map[value?.toString()] || value;
                    }

                    if (value && field.type === "date") {
                        defaultValue = (value as string).substring(0, 10);
                    }
                    else {
                        defaultValue = value;
                    }
                }
            }
            if (!defaultValue) {
                defaultValue = this.computeDefaultValue(field.default);
            }

            if (defaultValue) {
                dataModel[fieldName] = defaultValue;

                if (field.link) {
                    linkedHaveValues[fieldName] = true;
                }
            }
        }

        return [dataModel, linkedHaveValues];
    }

    private computeDefaultValue(defaultValue: string | number | undefined) {
        if (typeof defaultValue === "string" && defaultValue?.startsWith("{")) {
            const funcName = defaultValue.substring(1, defaultValue.length - 1);
            const localISO = DateTime.local().toISO();
            switch (funcName) {
                case "current_date":
                    return localISO.substring(0, 10);
                case "current_time":
                    return localISO.substring(11, 16);
                default:
                    // Unsupported function
                    return "";
            }
        }
        else {
            return defaultValue;
        }
    }

    private computeFunctionValue(func: FunctionSpec) {
        // Resolve values
        const values = [];
        for (const param of func.parameters) {
            switch (param.type) {
                case "field":
                    values.push(this.state.formData[param.value]);
                    break;
                case "value":
                    values.push(param.value);
                    break;
                default:
                    values.push(null);
                    break;
            }
        }

        switch (func.name) {
            case "sum":
                let sum = 0;
                for (const value of values) {
                    if (!!value || !!+value) {
                        sum += +value;
                    }
                }
                return sum;
            default:
                return "";
        }
    }

    private ensureRepeatersHaveBlanks(schema: any, data: any) {
        let content = schema.display.edit.content as any[];

        this.scanRenderElementContent(content, data);
    }

    private scanRenderElementContent(content: any[], data: any) {
        if (!content) {
            return;
        }

        for (const item of content) {
            if (item.type === "repeat") {
                // Ensure repeaters have an extra row like when editing a new document
                if (data[item.id] && (!item.max || data[item.id].length < item.max)) {
                    data[item.id].push({});
                }
            }

            if (Array.isArray(item.content)) {
                this.scanRenderElementContent(item.content, data);
            }
        }
    }

    private goBack() {
        if (this.state.isNewDocument) {
            this.props.history.go(this.state.isFromTemplate ? -3 : -2);
        }
        else {
            this.props.history.goBack();
        }
    }

    private async handleSaveHold() {
        try {
            this.setState({
                isSubmitting: true
            });

            await this.doSaveDocument(false);

            this.setState({
                isSubmitting: false,
            }, () => this.goBack());
        }
        catch (e) {
            this.setState({
                isSubmitting: e.message || "Could not save document"
            });
        }
    }

    private async handleSaveContinue() {
        try {
            this.setState({
                isSubmitting: true
            });

            const id = await this.doSaveDocument(false);

            if (this.state.isNewDocument) {
                // Switch URL to actual document
                this.props.history.replace(this.props.location.pathname.replace(/\/add\/.+$/, "/" + id));
            }

            this.setState({
                isSubmitting: false,
                isNewDocument: false,
            });
        }
        catch (e) {
            this.setState({
                isSubmitting: e.message || "Could not save document"
            });
        }
    }

    private preProcessDocument(data: { [name: string]: any }, isFinalize: boolean) {
        if (typeof data !== "object") {
            return data;
        }

        const fields = this.state.formSchema.fields;

        const properties = Object.keys(data);
        for (const prop of properties) {
            if (Array.isArray(data[prop])) {
                if (fields[prop]?.type === "repeat") {
                    const array = data[prop] as any[];

                    // Filter out empty rows, except for the last row unless we're finalizing the document
                    // Consider an unchecked checkbox as "empty" when considering a row
                    data[prop] = array
                        .map(a => (a !== null && typeof a === "object") ? this.preProcessDocument(a, isFinalize) : a)
                        .filter((a, i, arr) => (!isFinalize && i === arr.length - 1) || !Object.keys(a).every(b => a[b] === "" || a[b] === null || a[b] === undefined || (fields[b].type === "checkbox" && a[b] === false)));
                }
                else if (Array.isArray(data[prop]) && data[prop].length === 0) {
                    // Delete empty list array
                    delete data[prop];
                }
            }
            else if (data[prop] !== null && typeof data[prop] === "object") {
                data[prop] = this.preProcessDocument(data[prop], isFinalize);
            }
            else if (data[prop] === "" || data[prop] === null || data[prop] === undefined) {
                delete data[prop];
            }
        }

        return data;
    }

    private async doSaveDocument(isFinalize: boolean) {
        const formData = this.preProcessDocument(this.state.formData, !!isFinalize);

        const data = JSON.stringify(formData);

        if (this.state.isNewDocument) {
            const newForm = {
                documentTypeId: this.props.match.params.documentType,
                documentTypeVersion: this.state.documentVersion,
                data: data
            };

            // Creating the document and linking it to a patient are two separate operations
            // TODO: Determine what to do when the linking fails after a document is created
            const result = await V2.post<any>(`document`, newForm);
            await V2.put<void>(`patients/${this.state.patientId}/documents/link/${result.id}`);
            if (this.props.match.params.surgery_id) {
                // Link document to surgery too
                await V2.put<void>(`surgery/${this.props.match.params.surgery_id}/documents/link/${result.id}`);
            }

            this.setState({
                hasChanged: false
            });

            return result.id;
        }
        else if (this.state.isAmending) {
            // Amending a document
            const result = await V2.post<any>(`document/${this.props.match.params.documentId}/amend`, data);

            this.setState({
                hasChanged: false
            });

            return result.id;
        }
        else {
            await V2.put(`document/${this.props.match.params.documentId}/data`, data);

            this.setState({
                hasChanged: false
            });

            return this.props.match.params.documentId;
        }
    }

    private beginFinalizeDocument() {
        if (!this.validateForm(true)) {
            // Form didn't validate
            this.setState({
                isSubmitting: "Please fix the highlighted fields before finalizing."
            });

            return;
        }

        this.setState({
            isSubmitting: false
        });

        if (this.state.isFromTemplate) {
            this.handleSaveFinalize()
                .then(() => {
                    this.goBack();
                })
                .catch(e => {
                    this.setState({
                        isSubmitting: e.message || "Could not save document"
                    });
                });
        }
        else {
            this.toggleFinalizePrompt();
        }
    }

    private toggleFinalizePrompt() {
        const isFinalizeDialogOpen = this.state.isFinalizeDialogOpen;
        this.setState({
            isFinalizeDialogOpen: !isFinalizeDialogOpen
        });
    }

    private toggleDeletionDialog() {
        const isDeleteDialogOpen = this.state.isDeleteDialogOpen;
        this.setState({
            isDeleteDialogOpen: !isDeleteDialogOpen
        });
    }

    private async deleteDocument() {
        await V2.delete(`patients/${this.state.patientId}/documents/${this.props.match.params.documentId}`);

        this.setState({
            hasChanged: false
        });
    }

    private async handleSaveFinalize() {
        const documentId = await this.doSaveDocument(true);

        await V2.put(`document/${documentId}/finalize`);
    }

    private parseDataLink(link: string) {
        const parts = link.split(":");

        return {
            source: parts[0],
            path: parts[1]
        };
    }

    private toggleSignDialog(data?: ISignatureDialogReturnValues) {
        const isSignDialogOpen = this.state.isSignDialogOpen;

        if (!isSignDialogOpen) {
            // Find fields we're eligible to sign
            const availableSignatures = this.state.documentSignatures
                .map(a => ({ ...a, kind: documentSignatureKindToId(a.kind) }))
                .filter(a => !this.state.fieldSignatures[a.fieldId] && this.props.authContext.isInGroup(a.groupId));
            if (availableSignatures.length < 1) {
                return;
            }

            // Opening normally, remove callback
            this.setState({
                isSignDialogOpen: !isSignDialogOpen,
                inlineSignatureCallback: null,

                signatureDialogOptions: availableSignatures
            });
        }
        else {
            this.setState({
                isSignDialogOpen: !isSignDialogOpen,
            });
        }

        if (isSignDialogOpen && data) {
            this.signDocumentField(data);
        }
    }

    private hasAvailableSignatures() {
        return this.state.documentSignatures
            .some(a => !a.timestamp && this.props.authContext.isInGroup(a.groupId));
    }

    private handleSignButton() {
        this.toggleSignDialog();
    }

    private openSignatureDialogInline(e: MouseEvent<HTMLButtonElement>) {
        const name = e.currentTarget.name;
        const signatureKind = this.state.formSchema.fields[name].kind;

        this.setState({
            isSignDialogOpen: true,
            signatureMode: "field",
            signatureDialogOptions: [{ fieldId: name, kind: signatureKind }],
            inlineSignatureCallback: (data) => this.signDocumentField(data)
        });
    }

    private async signDocumentField(data: ISignatureDialogReturnValues) {
        let signData: any;
        switch (data.method) {
            case "electronic":
                signData = {
                    type: 0,// "electronic",
                    kind: data.kind,
                    fieldId: data.field,
                };
                break;
            case "pad":
                signData = {
                    type: 1,// "image",
                    kind: data.kind,
                    mime: data.mime,
                    data: data.data,
                    fieldId: data.field,
                };
                break;
        }

        // Convert kind text to numeric for server
        signData.kind = documentSignatureTextToKind(signData.kind);

        // Save, then request the full signature back from the server
        const signatureInfo = await V2.post<any>(`document/${this.props.match.params.documentId}/signature`, signData);
        const signature = await V2.get<any>(`document/${this.props.match.params.documentId}/signature/${signatureInfo.id}`);

        const fieldSignatures = this.state.fieldSignatures;
        // const documentInfo = this.state.documentInfo;
        const documentSignatures = this.state.documentSignatures;
        if (data.field) {
            fieldSignatures[data.field] = signature;

            const existingSignature = documentSignatures.find((a: any) => a.fieldId === data.field);
            if (signature) {
                Object.assign(existingSignature, signature);
            }

            documentSignatures.sort((a: any, b: any) => !a.timestamp
                ? 1
                : !b.timestamp
                    ? -1
                    : (a.timestamp || "").localeCompare(b.timestamp || ""));
        }

        this.setState({
            inlineSignatureCallback: null,
            fieldSignatures,
            documentSignatures
        });
    }

    private async signDocument(signatureData: ISignatureDialogReturnValues) {
        try {
            this.setState({
                isSubmitting: true
            });

            let kind = documentSignatureTextToKind(signatureData.kind || "unknown");

            const data = {
                kind
            };

            await V2.post<void>(`document/${this.props.match.params.documentId}/signature`, data);

            this.setState({
                isSubmitting: false
            }, () => this.loadData());
        }
        catch (e) {
            this.setState({
                isSubmitting: e.message || "Could not sign document"
            });
        }
    }

    private amendDocument() {
        const { formSchema, formData } = this.state;
        this.ensureRepeatersHaveBlanks(formSchema, formData);

        this.setState({
            isAmending: true,
            isReadOnly: false,
            formData
        });
    }

    private openTemplateDialog(targetField: string, templateGroup: string) {
        this.setState({
            isTemplateDialogOpen: true,
            templateTargetFieldId: targetField,
            templateGroup: templateGroup
        });
    }

    private closeTemplateDialog() {
        this.setState({
            isTemplateDialogOpen: false,
            templateTargetFieldId: null,
            templateGroup: null
        });
    }

    private handleTemplateFinished(text: string) {
        if (!this.state.templateTargetFieldId) {
            console.warn("No target field ID was specified for template");
            return;
        }

        const { formData } = this.state;

        let data = formData[this.state.templateTargetFieldId];
        if (data === undefined) {
            data = "";
        }

        data += text;

        formData[this.state.templateTargetFieldId] = data;

        this.setState({
            hasChanged: true,
            formData
        });
    }

    private async lockDocument() {
        try {
            await V2.put(`document/${this.props.match.params.documentId}/lock`);

            if (this.state.hasChanged) {
                await this.handleSaveContinue();
            }
            await this.loadData();
        }
        catch (e) {
            // TODO: Report this error
        }
    }

    private async unlockDocument() {
        try {
            await V2.delete(`document/${this.props.match.params.documentId}/lock`);

            if (this.state.hasChanged) {
                await this.handleSaveContinue();
            }
            this.loadData();
        }
        catch (e) {
            // TODO: Report this error
        }
    }

    private toggleSaveTemplateDialog() {
        const isSaveTemplateDialogOpen = this.state.isSaveTemplateDialogOpen;
        this.setState({
            isSaveTemplateDialogOpen: !isSaveTemplateDialogOpen
        });
    }

    private renderSaveTemplateDialog() {
        return <SaveTemplateDialog
            isOpen={this.state.isSaveTemplateDialogOpen}
            onRequestClose={this.toggleSaveTemplateDialog}
            onSave={this.saveAsTemplate} />
    }

    private async saveAsTemplate(info: SaveTemplateInfo) {
        const formData = this.preProcessDocument(this.state.formData, !info.allowChanges);
        console.log(formData);

        const data = {
            isFinalized: !info.allowChanges,
            name: info.name,
            data: JSON.stringify(formData)
        }

        const type = this.state.documentInfo?.documentTypeId || this.props.match.params.documentType;
        if (!type) {
            throw new Error("Unknown document type");
        }
            
        await V2.post<void>(`document/types/${type}/templates`, data);
    }
}

type SaveTemplateInfo = {
    name: string;
    allowChanges: boolean;
};

interface ISaveTemplateDialogProps {
    isOpen: boolean;
    onRequestClose: () => void;
    onSave: (info: SaveTemplateInfo) => Promise<void>;
}

function SaveTemplateDialog(props: ISaveTemplateDialogProps) {
    const [allowChanges, setAllowChanges] = useState<boolean>(true);
    const [name, setName] = useState<string>("");
    const [isSaving, setIsSaving] = useState<boolean | string>(false);

    const doSave = () => {
        setIsSaving(true);
        props.onSave({ name: name, allowChanges: allowChanges })
            .then(() => {
                setIsSaving(false);
                props.onRequestClose();
            })
            .catch(e => {
                setIsSaving(e.message || "Could not save template");
            });
    };

    return props.isOpen
        ? <ReactModal isOpen={props.isOpen} onRequestClose={props.onRequestClose}
            overlayClassName="modal-overlay" className="modal-content modal-content-500" portalClassName="modal-portal"
            bodyOpenClassName="modal-open"
            contentLabel="Sign dialog">
            <section className="info-block input-form">
                <header>
                    <h3>Save as Template</h3>
                </header>

                <section>
                    <div className="input-form-group">
                        <label htmlFor="template-name">Name</label>
                        <input type="text" name="template-name" value={name} onChange={e => setName(e.currentTarget.value)} autoComplete="off" autoFocus={true} />
                    </div>
                </section>
                <section>
                    <div className="input-form-group">
                        <div>Should the template be editable when used?</div>
                        <Checkbox fancy={true} id="save-template-modal-finalized" checked={allowChanges} onChange={e => setAllowChanges(e.currentTarget.checked)}>Allow changes</Checkbox>
                    </div>
                </section>
                {isSaving === true
                    ? <div className="spinner lds-dual-ring" />
                    : typeof isSaving === "string"
                        ? <div className="error-text">{isSaving}</div>
                        : null}
                <div className="action-panel-dialog">
                    <button type="submit" className="button green push-right" disabled={isSaving === true} onClick={doSave}>Save</button>
                    <button type="button" className="button" disabled={isSaving === true} onClick={props.onRequestClose}>Close</button>
                </div>
            </section>
        </ReactModal>
        : null;
}

export default withAuth(withTitle("Edit Document", PatientDocumentEditView));
